/**
 * Store de cache centralisé pour tous les assets
 * Optimise les appels API et gère les statistiques du dashboard
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAuthStore } from './auth';

// Services API
import { agents } from '@/api/agents';
import { collections } from '@/api/collections';
import { digitalEntities } from '@/api/digitalEntities';
import { locations } from '@/api/locations';
import { materialEntities } from '@/api/materialEntities';
import { memos } from '@/api/memos';
import { projects } from '@/api/projects';
import { tags } from '@/api/public';

// Types
import type { Tags } from '@/api/public';
import type { Entity } from '@/api/types/core/common';
import type { Agent } from '@/api/types/entities/agent/types';
import type { DigitalEntity } from '@/api/types/entities/digitalEntity/types';
import type { Location } from '@/api/types/entities/location/types';
import type { MaterialEntity } from '@/api/types/entities/materialEntity/types';
import type { Project } from '@/api/types/entities/project/types';
import type { MemoCollection } from '@/api/types/memos/collections';
import type { Memo } from '@/api/types/memos/types';

// Types de cache
interface CacheEntry<T> {
  data: Map<string, T>;
  timestamp: number;
  isLoading: boolean;
}

interface AssetStats {
  total: number;
  my: number;
  public: number;
}

type AssetType = 'agents' | 'locations' | 'digitalEntities' | 'materialEntities' | 'projects' | 'memos' | 'collections';
type AssetEntity = Agent | Location | DigitalEntity | MaterialEntity | Project | Memo | MemoCollection;

// Configuration du cache (en millisecondes)
const CACHE_DURATION = {
  SHORT: 2 * 60 * 1000,    // 2 minutes pour les données fréquemment modifiées
  MEDIUM: 5 * 60 * 1000,   // 5 minutes pour les données moyennement modifiées
  LONG: 10 * 60 * 1000     // 10 minutes pour les données rarement modifiées
};

export const useAssetsCacheStore = defineStore('assetsCache', () => {
  const authStore = useAuthStore();

  // ===============================
  // État du cache
  // ===============================
  
  const agentsCache = ref<CacheEntry<Agent>>({ data: new Map(), timestamp: 0, isLoading: false });
  const locationsCache = ref<CacheEntry<Location>>({ data: new Map(), timestamp: 0, isLoading: false });
  const digitalEntitiesCache = ref<CacheEntry<DigitalEntity>>({ data: new Map(), timestamp: 0, isLoading: false });
  const materialEntitiesCache = ref<CacheEntry<MaterialEntity>>({ data: new Map(), timestamp: 0, isLoading: false });
  const projectsCache = ref<CacheEntry<Project>>({ data: new Map(), timestamp: 0, isLoading: false });
  const memosCache = ref<CacheEntry<Memo>>({ data: new Map(), timestamp: 0, isLoading: false });
  const collectionsCache = ref<CacheEntry<MemoCollection>>({ data: new Map(), timestamp: 0, isLoading: false });
  
  const tagsCache = ref<CacheEntry<Tags>>({ data: new Map(), timestamp: 0, isLoading: false });

  // ===============================
  // Helpers de cache
  // ===============================

  const isCacheValid = (timestamp: number, duration: number = CACHE_DURATION.MEDIUM): boolean => {
    return Date.now() - timestamp < duration;
  };

  const getCacheEntry = (assetType: AssetType): CacheEntry<AssetEntity> => {
    switch (assetType) {
      case 'agents': return agentsCache.value as CacheEntry<AssetEntity>;
      case 'locations': return locationsCache.value as CacheEntry<AssetEntity>;
      case 'digitalEntities': return digitalEntitiesCache.value as CacheEntry<AssetEntity>;
      case 'materialEntities': return materialEntitiesCache.value as CacheEntry<AssetEntity>;
      case 'projects': return projectsCache.value as CacheEntry<AssetEntity>;
      case 'memos': return memosCache.value as CacheEntry<AssetEntity>;
      case 'collections': return collectionsCache.value as CacheEntry<AssetEntity>;
      default: throw new Error(`Type d'asset non supporté: ${assetType}`);
    }
  };

  const setCacheEntry = (assetType: AssetType, dataArray: AssetEntity[], isLoading: boolean = false): void => {
    // Transformer le tableau en Map
    const dataMap = new Map<string, AssetEntity>();
    for (const item of dataArray) {
      dataMap.set(item.uuid, item);
    }

    const entry = {
      data: dataMap,
      timestamp: Date.now(),
      isLoading
    };

    switch (assetType) {
      case 'agents': agentsCache.value = entry as CacheEntry<Agent>; break;
      case 'locations': locationsCache.value = entry as CacheEntry<Location>; break;
      case 'digitalEntities': digitalEntitiesCache.value = entry as CacheEntry<DigitalEntity>; break;
      case 'materialEntities': materialEntitiesCache.value = entry as CacheEntry<MaterialEntity>; break;
      case 'projects': projectsCache.value = entry as CacheEntry<Project>; break;
      case 'memos': memosCache.value = entry as CacheEntry<Memo>; break;
      case 'collections': collectionsCache.value = entry as CacheEntry<MemoCollection>; break;
    }
  };

  // ===============================
  // Services API par type
  // ===============================

  const getApiService = (assetType: AssetType): { getAccessible: () => Promise<AssetEntity[]> } => {
    switch (assetType) {
      case 'agents': return agents;
      case 'locations': return locations;
      case 'digitalEntities': return digitalEntities;
      case 'materialEntities': return materialEntities;
      case 'projects': return projects;
      case 'memos': return memos;
      case 'collections': return collections as any; // any pour correspondre à la signature générale
      default: throw new Error(`Service API non trouvé pour: ${assetType}`);
    }
  };

  // ===============================
  // Méthodes principales
  // ===============================

  /**
   * Récupère les assets avec cache intelligent
   */
  const getAssets = async <T>(
    assetType: AssetType,
    forceRefresh: boolean = false,
    cacheDuration: number = CACHE_DURATION.MEDIUM
  ): Promise<T[]> => {
    const cache = getCacheEntry(assetType);
    
    // Vérifier si le cache est valide et qu'on ne force pas le refresh
    if (!forceRefresh && isCacheValid(cache.timestamp, cacheDuration) && cache.data.size > 0) {
      return Array.from(cache.data.values()) as T[];
    }

    // Éviter les appels multiples simultanés
    if (cache.isLoading) {
      // Attendre un peu et retourner les données du cache si disponibles
      await new Promise(resolve => setTimeout(resolve, 100));
      return Array.from(cache.data.values()) as T[];
    }

    try {
      // Marquer comme en cours de chargement
      setCacheEntry(assetType, Array.from(cache.data.values()), true);

      const apiService = getApiService(assetType);
      const data = await apiService.getAccessible();
      
      // Mettre à jour le cache
      setCacheEntry(assetType, data, false);
      
      return data as T[];
    } catch (error) {
      console.error(`Erreur lors du chargement des ${assetType}:`, error);
      // Remettre isLoading à false en cas d'erreur
      setCacheEntry(assetType, Array.from(cache.data.values()), false);
      throw error;
    }
  };

  /**
   * Récupère uniquement les assets de l'utilisateur
   */
  const getMyAssets = async <T extends Entity>(assetType: AssetType, forceRefresh: boolean = false): Promise<T[]> => {
    const allAssets = await getAssets<T>(assetType, forceRefresh);
    const userId = authStore.user?.uuid;
    
    if (!userId) return [];
    
    return allAssets.filter((asset: T) => asset.user_id === userId);
  };

  /**
   * Récupère uniquement les assets publics
   */
  const getPublicAssets = async <T extends Entity>(assetType: AssetType, forceRefresh: boolean = false): Promise<T[]> => {
    const allAssets = await getAssets<T>(assetType, forceRefresh);
    const userId = authStore.user?.uuid;
    
    return allAssets.filter((asset: T) => asset.is_public && asset.user_id !== userId);
  };

  /**
   * Met à jour un asset dans le cache après modification
   */
  const updateAssetInCache = (assetType: AssetType, updatedAsset: AssetEntity): void => {
    const cache = getCacheEntry(assetType);
    if (cache.data.has(updatedAsset.uuid)) {
      cache.data.set(updatedAsset.uuid, updatedAsset);
    } else {
      cache.data.set(updatedAsset.uuid, updatedAsset);
    }
    setCacheEntry(assetType, Array.from(cache.data.values()), false);
  };

  /**
   * Supprime un asset du cache
   */
  const removeAssetFromCache = (assetType: AssetType, assetUuid: string): void => {
    const cache = getCacheEntry(assetType);
    cache.data.delete(assetUuid);
    setCacheEntry(assetType, Array.from(cache.data.values()), false);
  };

  /**
   * Ajoute un nouvel asset au cache
   */
  const addAssetToCache = (assetType: AssetType, newAsset: AssetEntity): void => {
    const cache = getCacheEntry(assetType);
    if (cache.data.has(newAsset.uuid)) {
      return; // Déjà présent
    }
    cache.data.set(newAsset.uuid, newAsset);
    setCacheEntry(assetType, Array.from(cache.data.values()), false);
  };

  /**
   * Invalide le cache pour un type d'asset
   */
  const invalidateCache = (assetType: AssetType): void => {
    setCacheEntry(assetType, [], false);
  };

  /**
   * Invalide tout le cache
   */
  const invalidateAllCache = (): void => {
    const assetTypes: AssetType[] = ['agents', 'locations', 'digitalEntities', 'materialEntities', 'projects', 'memos', 'collections'];
    assetTypes.forEach(type => invalidateCache(type));
  };

  /**
   * Précharge tous les assets (pour le dashboard)
   */
  const preloadAllAssets = async (): Promise<void> => {
    const assetTypes: AssetType[] = ['agents', 'locations', 'digitalEntities', 'materialEntities', 'projects', 'memos', 'collections'];
    
    // Charger en parallèle mais avec gestion d'erreur individuelle
    const promises = assetTypes.map(async (type) => {
      try {
        await getAssets(type, false, CACHE_DURATION.LONG);
      } catch (error) {
        console.warn(`Erreur lors du préchargement de ${type}:`, error);
      }
    });
    
    await Promise.allSettled(promises);
  };

  // ===============================
  // Statistiques calculées
  // ===============================

  const getAssetStats = (assetType: AssetType): AssetStats => {
    const cache = getCacheEntry(assetType);
    const userId = authStore.user?.uuid;
    
    if (!userId || cache.data.size === 0) {
      return { total: 0, my: 0, public: 0 };
    }

    const myAssets = Array.from(cache.data.values()).filter((asset: AssetEntity) => asset.user_id === userId);
    const publicAssets = Array.from(cache.data.values()).filter((asset: AssetEntity) => asset.is_public && asset.user_id !== userId);
    
    return {
      total: cache.data.size,
      my: myAssets.length,
      public: publicAssets.length
    };
  };

  // Statistiques réactives pour le dashboard
  const agentsStats = computed(() => getAssetStats('agents'));
  const locationsStats = computed(() => getAssetStats('locations'));
  const digitalEntitiesStats = computed(() => getAssetStats('digitalEntities'));
  const materialEntitiesStats = computed(() => getAssetStats('materialEntities'));
  const projectsStats = computed(() => getAssetStats('projects'));
  const memosStats = computed(() => getAssetStats('memos'));
  const collectionsStats = computed(() => getAssetStats('collections'));

  // États de chargement
  const isLoadingAssets = computed((): boolean => {
    return agentsCache.value.isLoading ||
           locationsCache.value.isLoading ||
           digitalEntitiesCache.value.isLoading ||
           materialEntitiesCache.value.isLoading ||
           projectsCache.value.isLoading ||
           memosCache.value.isLoading ||
           collectionsCache.value.isLoading;
  });

  // ===============================
  // Gestion des tags
  // ===============================

  const getTags = async (forceRefresh: boolean = false): Promise<Tags[]> => {
    const cache = tagsCache.value;
    
    // Vérifier si le cache est valide et qu'on ne force pas le refresh
    if (!forceRefresh && isCacheValid(cache.timestamp, CACHE_DURATION.LONG) && cache.data.size > 0) {
      return Array.from(cache.data.values());
    }

    // Éviter les appels multiples simultanés
    if (cache.isLoading) {
      // Attendre que le chargement en cours se termine
      while (cache.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return Array.from(cache.data.values());
    }

    try {
      cache.isLoading = true;
      const data = await tags.fetchTags();
      
      // Correction pour le cache des tags (qui n'ont pas d'UUID)
      const tagsMap = new Map<string, Tags>();
      data.forEach((tagGroup, index) => tagsMap.set(`group_${index}`, tagGroup));
      cache.data = tagsMap as any; // any est nécessaire ici car la structure est différente
      
      cache.timestamp = Date.now();
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des tags:', error);
      throw error;
    } finally {
      cache.isLoading = false;
    }
  };

  const invalidateTagsCache = (): void => {
    tagsCache.value = { data: new Map(), timestamp: 0, isLoading: false };
  };

  /**
   * Récupère un asset spécifique par son UUID depuis le cache.
   * Utilise l'accès direct par clé pour une performance maximale.
   */
  async function getAssetByUuid<T extends Entity>(assetType: AssetType, uuid: string): Promise<T | null> {
    if (!uuid) return null;
    
    await getAssets(assetType);

    const cache = getCacheEntry(assetType);
    if (cache.data) {
      // Accès direct au Map
      return (cache.data.get(uuid) as T) || null;
    }
    return null;
  }

  // ===============================
  // Interface du store
  // ===============================

  return {
    // Méthodes principales
    getAssets,
    getMyAssets,
    getPublicAssets,
    getAssetByUuid,
    
    // Gestion du cache
    updateAssetInCache,
    removeAssetFromCache,
    addAssetToCache,
    invalidateCache,
    invalidateAllCache,
    preloadAllAssets,
    
    // Gestion des tags
    getTags,
    invalidateTagsCache,
    
    // État de chargement
    isLoadingAssets,
    
    // Statistiques
    agentsStats,
    locationsStats,
    digitalEntitiesStats,
    materialEntitiesStats,
    projectsStats,
    memosStats,
    collectionsStats,
    
    // Accès direct aux caches (pour debug)
    agentsCache: computed(() => agentsCache.value),
    locationsCache: computed(() => locationsCache.value),
    digitalEntitiesCache: computed(() => digitalEntitiesCache.value),
    materialEntitiesCache: computed(() => materialEntitiesCache.value),
    projectsCache: computed(() => projectsCache.value),
    memosCache: computed(() => memosCache.value),
    collectionsCache: computed(() => collectionsCache.value),
  };
}); 